import { useMemo } from "react";
import { FIELD_NAME_MAP, FIELD_DB_MAP } from 'constants/jobs.constants';

// Only the exact "ALL - All" sentinel (same rule as backend _is_select_all),
// so a real option like "Tools - All" stays a real value.
export const isSelectAll = (value) =>
    typeof value === 'string' &&
    value.replace(/_/g, '-').split('-').map((p) => p.trim().toLowerCase()).join('-') === 'all-all';

// Drops values no longer present in valid options after a parent-level change
export const filterSelected = (selected, validOptions) => {
    const validSet = new Set(validOptions.map((o) => o.value));
    return (Array.isArray(selected) ? selected : []).filter((v) => validSet.has(v));
};

// Replaces sentinel values (e.g. "ALL - All") with every real option from the list.
// If the only available option is the sentinel itself, keep it so the selection is
// not silently dropped.
export const expandSentinels = (values = [], options = []) => {
    if (!values.some(isSelectAll)) return values;
    const realValues = options.filter((o) => !isSelectAll(o.value)).map((o) => o.value);
    if (realValues.length) return realValues;
    return values.filter(isSelectAll).slice(0, 1);
};

// Resolve a stored multi-select value (a comma-joined string) into an array of
// real option names. Option names can themselves contain commas so a plain comma
// split would break one name into several. We match against the known option list with a greedy
// longest-first match, and only fall back to a comma split if nothing matches.
export const resolveMulti = (stored, optionNames = []) => {
    if (Array.isArray(stored)) return stored;
    if (typeof stored !== 'string' || !stored.trim()) return [];
    const text = stored.trim();

    // Whole string is exactly one option (covers names containing commas).
    if (optionNames.includes(text)) return [text];

    // Greedy: consume the longest option name at the current position, repeat.
    const sorted = [...optionNames].sort((a, b) => b.length - a.length);
    const result = [];
    let rest = text;
    while (rest.length) {
        const current = rest;
        const match = sorted.find(name => current === name || current.startsWith(`${name},`));
        if (!match) break;
        result.push(match);
        rest = current.slice(match.length).replace(/^\s*,\s*/, '');
    }
    if (result.length) return result;

    // No option list (or no match) — fall back to the naive comma split.
    return text.split(',').map(s => s.trim()).filter(Boolean);
};

export const joinMulti = (v) =>
    (Array.isArray(v) ? v.filter(x => !isSelectAll(x)).join(', ') : (v ?? ''));

// Sorted join so the stored value (and duplicate key) doesn't depend on pick order
export const joinSortedMulti = (v) => joinMulti(Array.isArray(v) ? [...v].sort() : v);

// ── Options Config ─────────────────────────────────────────────────────────────

const OPTIONS_CONFIG = {
    company_structure: {
        getData: (source, key) => {
            const cs = source?.company_structure;
            if (Array.isArray(cs)) return cs.find(item => item[key])?.[key];
            if (cs && typeof cs === "object") return cs[key];
            return undefined;
        },
        maps: {
            role_type: v => ({ value: v, label: v }),
            Work_Environment: v => ({ value: v, label: v }),
            job_grade: item =>
                item?.level && item?.grade_title
                    ? { value: item.grade_title, label: `${item.level} - ${item.grade_title}` }
                    : { value: item, label: item },
            report_to: item => ({
                value: `${item.level} - ${item.grade_title}`,
                label: `${item.level} - ${item.grade_title}`,
            }),
        },
    },
    jobs: {
        getData: source => source?.jobs,
        map: job => ({
            value: job.job_code,
            label: `${job.job_code} - ${job.job_family_name}`,
            allFields: job,
        }),
    },
    legal_entities: {
        getData: source => source?.organization_structure,
        map: entity => ({ value: entity.legal_entity, label: entity.legal_entity }),
    },
};

export const useOptions = (type, key, source) =>
    useMemo(() => {
        if (!source || !OPTIONS_CONFIG[type]) return [];
        const config = OPTIONS_CONFIG[type];
        const rawData = type === "company_structure" ? config.getData(source, key) : config.getData(source);
        const data = Array.isArray(rawData) ? rawData : rawData ? [rawData] : [];
        if (type === "company_structure") {
            const mapFn = config.maps[key] || (v => ({ value: v, label: v }));
            return data.map(mapFn);
        }
        return data.map(config.map);
    }, [type, key, source]);

// ── Auto-select helper ─────────────────────────────────────────────────────────

export const handleAutoSelect = ({ selectedOption, resetForm, setFieldValue, jobGradeValue, setSelectedJobFields }) => {
    if (!selectedOption) {
        resetForm();
        setSelectedJobFields(null);
        return;
    }
    const job = selectedOption.allFields;
    setSelectedJobFields(job);
    const fieldMap = {
        jobCode: job.job_code,
        roleTitle: job.job_title,
        altRoleTitle: job.job_title,
        legalEntity: job.legal_entity,
        businessUnit: job.business_unit,
        // function / sub-function / specialty are multi-select — normalize to arrays
        functionDepartment: job.function ? [job.function] : [],
        subFunction: job.sub_function ? [job.sub_function] : [],
        specialty: job.specialty ? [job.specialty] : [],
        roleType: job.role_type,
        jobGrade: jobGradeValue(job.level),
        workEnvironment: job.work_environment,
        reportingTo: job.reporting_to,
    };
    Object.entries(fieldMap).forEach(([key, value]) => setFieldValue(key, value ?? ''));
};

// ── Error label resolution ─────────────────────────────────────────────────────

export const resolveErrorLabel = (errorFields, getConfig) => {
    if (!errorFields?.length) return null;
    const firstField = errorFields[0]?.name?.[0];
    if (!firstField) return null;
    const configKey = FIELD_NAME_MAP[firstField];
    const config = getConfig(configKey);
    return config?.label ?? (configKey?.charAt(0).toUpperCase() + configKey?.slice(1));
};

// ── Duplicate detection ────────────────────────────────────────────────────────


export const getDuplicateDetectionConfig = (orgMetaData) => {
    const cfg = orgMetaData?.field_configuration?.[0]?.duplicate_detection || {};
    return { jobCode: cfg.entity_job_code, jobRequisition: cfg.entity_job_requisition };
};

export const isFieldDisabledByDuplicateDetection = (fieldName, duplicateConfig) => {
    const { jobCode, jobRequisition } = duplicateConfig || {};
    const mapped = FIELD_DB_MAP[fieldName] || fieldName;
    const inJobCode = jobCode?.enforce_check && jobCode?.unique_keys?.includes(mapped);
    const inJobReq = jobRequisition?.enforce_check && jobRequisition?.unique_keys?.includes(mapped);
    if (jobCode?.enforce_check && jobRequisition?.enforce_check) return inJobCode || inJobReq;
    if (jobCode?.enforce_check) return inJobCode;
    if (jobRequisition?.enforce_check) return inJobReq;
    return false;
};
